import { useCallback, useEffect, useRef, useState } from "react";
import { getConversation, getMessage } from "../api";
import type { MailConversation, MailListView, MailSource } from "../types";

export type MailConversationState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "loaded"; conversation: MailConversation }
  | { status: "notFound" }
  | { status: "error" };

/**
 * Loads what the reading pane shows for the selected email: its whole conversation in the
 * conversations view, or just the email in the messages view, as a conversation of one so the
 * pane renders both the same way. Only the latest selection is applied, so moving quickly
 * through the list never shows something other than what is selected.
 */
export const useMailConversation = (mailId: string | null, view: MailListView, source: MailSource) => {
  const [state, setState] = useState<MailConversationState>({ status: "idle" });
  const requestId = useRef(0);
  const companyId = source.kind === "customer" ? source.companyId : null;

  const load = useCallback(async () => {
    const currentRequest = ++requestId.current;

    if (!mailId) {
      setState({ status: "idle" });
      return;
    }

    setState({ status: "loading" });

    try {
      const scope: MailSource = companyId ? { kind: "customer", companyId } : { kind: "mine" };
      const conversation = view === "conversations"
        ? await getConversation(mailId, scope)
        : await getMessage(mailId).then(message => message
          ? { threadId: message.threadId, messages: [message], hasEarlier: false }
          : null);

      if (currentRequest !== requestId.current) return;
      setState(conversation ? { status: "loaded", conversation } : { status: "notFound" });
    } catch {
      if (currentRequest !== requestId.current) return;
      setState({ status: "error" });
    }
  }, [mailId, view, companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => () => {
    requestId.current++;
  }, []);

  return { state, retry: load };
};
